import { Button, LoadingText } from "@naf/teamscheme";
import {
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import {
  AccidentReportPage,
  type AccidentReportViewModel,
  type VehicleKey,
} from "#accident-statement/schema";
import { type ActionClient, useClientBase } from "../api/ClientBase";
import { useReportClient } from "../api/ReportClient";
import MagnifyingGlassSvg from "../graphics/icons/magnifying-glass-plus.svg";
import ErrorMessage from "../shared/elements/ErrorMessage";
import ZoomableScrollImage from "../shared/elements/ZoomableImage";
import { useLoadOncePerLocation } from "../shared/utils/useLoadOncePerLocation";
import { useParty, useReport } from "./ReportContext";
import type { TemporaryUrl } from "./page-one/attachments/AttachmentsClient";
import { useOnUpdate } from "./updates/ReportUpdates";
import { ReportChangeType } from "./updates/ReportChangeType";

function useDocumentPreviewClient(
  page: AccidentReportPage,
): ActionClient<void, PublicDocumentPreviewViewModel> {
  const { id } = useReport();
  const client = useReportClient();
  const clientBase = useClientBase<void, PublicDocumentPreviewViewModel>(
    useCallback(async () => {
      const preview = await client.get<PublicDocumentPreviewViewModel>(
        `report/${id}/${page}/document/preview`,
      );

      if (!preview) {
        throw new Error("Document preview not found");
      }

      return preview;
    }, [client, id, page]),
  );

  return clientBase;
}

interface ReportPreviewPageContext
  extends ActionClient<void, PublicDocumentPreviewViewModel> {
  loadOncePerLocation: () => void;
}

type IReportPreviewContext = {
  [page in AccidentReportPage]: ReportPreviewPageContext;
};

const ReportPreviewContext = createContext<IReportPreviewContext | null>(null);

function useAutoLoadedDocumentPreview(
  context: ActionClient<void, PublicDocumentPreviewViewModel>,
  getHash: (report: AccidentReportViewModel, party: VehicleKey) => string,
): ReportPreviewPageContext {
  const { load, loadedLocation } = useLoadOncePerLocation();
  const { fetch: fetchPreview } = context;
  const report = useReport();
  const party = useParty();
  const documentHash = getHash(report, party);

  useEffect(() => {
    if (!loadedLocation || !documentHash) return;

    const cancelable = fetchPreview();

    return () => cancelable?.cancel();
  }, [fetchPreview, loadedLocation, documentHash]);

  return { ...context, loadOncePerLocation: load };
}

export function ReportPreviewProvider({ children }: { children: ReactNode }) {
  const pageOneContext = useAutoLoadedDocumentPreview(
    useDocumentPreviewClient(AccidentReportPage.PageOne),
    (x) => x.documentHash,
  );
  const pageTwoContext = useAutoLoadedDocumentPreview(
    useDocumentPreviewClient(AccidentReportPage.PageTwo),
    (x) =>
      "pageTwoA" in x ? x.pageTwoA.documentHash : x.pageTwoB.documentHash,
  );

  return (
    <ReportPreviewContext.Provider
      value={{
        [AccidentReportPage.PageOne]: pageOneContext,
        [AccidentReportPage.PageTwo]: pageTwoContext,
      }}
    >
      {children}
    </ReportPreviewContext.Provider>
  );
}

export function useReportPreviewClient() {
  const context = useContext(ReportPreviewContext);
  if (!context) throw new Error("No ReportPreviewContext found!");
  return context;
}

export function useReportPageOnePreview({
  refreshOnSignatureUpdate = false,
}: {
  refreshOnSignatureUpdate?: boolean;
} = {}) {
  const context = useReportPreviewClient()[AccidentReportPage.PageOne];
  const {
    loadOncePerLocation,
    fetch: fetchPreview,
    isLoading: isPreviewLoading,
  } = context;

  const isPreviewLoadingRef = useRef(isPreviewLoading);

  const onSignatureUpdate = useCallback(() => {
    if (!refreshOnSignatureUpdate || isPreviewLoadingRef.current) {
      return;
    }

    fetchPreview();
  }, [refreshOnSignatureUpdate, fetchPreview]);

  useOnUpdate(ReportChangeType.Signature, onSignatureUpdate);

  useEffect(() => {
    loadOncePerLocation();
  }, [loadOncePerLocation]);

  return context;
}

interface PublicDocumentPreviewViewModel {
  hash: string;
  url: TemporaryUrl;
}

const DocumentPreviewContainer = styled.div`
  position: relative;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: white;
`;

const DocumentPreviewPlaceholder = styled.div`
  background: white;
  flex: 1 0 auto;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: absolute;
  padding: 4em 1em 1em;
  text-align: center;
`;

const DocumentPreview = styled(ZoomableScrollImage)`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: auto;
  z-index: 1;
`;

export type ReportPreviewClient = ActionClient<
  void,
  PublicDocumentPreviewViewModel
>;
export interface ReportPreviewProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "onClick" | "children"> {
  preview: ReportPreviewClient;
  changed?: boolean;
}

const CompactReportPreviewContainer = styled.div`
  position: relative;
  isolation: isolate;
  width: 100%;
  max-width: 720px;
  padding-top: ${((8 * 31) / 720) * 100}%;
  overflow: hidden;
`;

const Zoom = styled.div`
  padding: ${({ theme }) => theme.spacing.space24}
    ${({ theme }) => theme.spacing.space32};
  border-radius: ${({ theme }) => theme.radius.s};
  background: white;
  display: flex;
  align-items: center;
  transition: box-shadow 0.25s ease;
  ${({ theme }) => theme.fontStyle.bodyText.bodyText};

  img {
    width: 1.5em;
    height: 1.5em;
    margin-right: 1em;
  }
`;

const Overlay = styled(function (
  props: Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children">,
) {
  return (
    <button {...props}>
      <Zoom>
        <img alt="Zoom" src={MagnifyingGlassSvg} />
        <span>Forhåndsvis</span>
      </Zoom>
    </button>
  );
})`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  cursor: zoom-in;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  width: 100%;
  border: none;
  margin: 0;
  padding: 0;
  background: rgba(0, 0, 0, 0.1);
  z-index: 2;
`;

export function CompactReportPreview(props: ReportPreviewProps) {
  const { preview, changed = false, ...divProps } = props;
  const { isLoading, result } = preview;
  const [isZoomed, setIsZoomed] = useState(false);

  return (
    <div {...divProps}>
      <CompactReportPreviewContainer>
        {isLoading ? (
          <DocumentPreviewPlaceholder>
            <LoadingText
              text={
                !changed
                  ? "Henter forhåndsvisning"
                  : "Din motpart har oppdatert sin del av skademeldingen. Henter ny forhåndsvisning"
              }
            />
          </DocumentPreviewPlaceholder>
        ) : null}
        {result && !isLoading ? (
          <>
            <Overlay onClick={() => setIsZoomed(true)} />
            <DocumentPreview
              alt=""
              src={result.url.href}
              isZoomed={isZoomed}
              setIsZoomed={setIsZoomed}
            />
          </>
        ) : preview.error && !preview.isLoading ? (
          <DocumentPreviewPlaceholder>
            <ErrorMessage>
              Kunne ikke laste forhåndsvisning.{" "}
              <Button onClick={() => preview.fetch()}>Prøv igjen</Button>
            </ErrorMessage>
          </DocumentPreviewPlaceholder>
        ) : null}
      </CompactReportPreviewContainer>
    </div>
  );
}

export default function ReportPreview(props: ReportPreviewProps) {
  const { preview, changed = false, ...divProps } = props;
  const { isLoading, result } = preview;
  const signed = false;
  const [imageDimensions, setImageDimesions] = useState<{
    height: number;
    width: number;
  } | null>(null);
  const [isZoomed, setIsZoomed] = useState(false);

  return (
    <div {...divProps}>
      <DocumentPreviewContainer
        style={{
          paddingTop: imageDimensions
            ? `${(imageDimensions.height / imageDimensions.width) * 100}%`
            : `${Math.sqrt(2) * (signed ? 2 : 1) * 100}%`,
        }}
      >
        {isLoading || !imageDimensions ? (
          <DocumentPreviewPlaceholder>
            <LoadingText
              text={
                !changed
                  ? "Henter forhåndsvisning"
                  : "Din motpart har oppdatert sin del av skademeldingen. Henter ny forhåndsvisning"
              }
            />
          </DocumentPreviewPlaceholder>
        ) : null}
        {result && !isLoading ? (
          <DocumentPreview
            onLoad={(event) => {
              const { height, width } = (
                event.target as HTMLImageElement
              ).getBoundingClientRect();

              setImageDimesions({ height, width });
            }}
            alt=""
            src={result.url.href}
            isZoomed={isZoomed}
            setIsZoomed={setIsZoomed}
            onClick={() => setIsZoomed(true)}
          />
        ) : preview.error && !preview.isLoading ? (
          <DocumentPreviewPlaceholder>
            <ErrorMessage>
              Kunne ikke laste forhåndsvisning.{" "}
              <Button onClick={() => preview.fetch()}>Prøv igjen</Button>
            </ErrorMessage>
          </DocumentPreviewPlaceholder>
        ) : null}
      </DocumentPreviewContainer>
    </div>
  );
}
